import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDateTime, groupIban } from "@/lib/format";
import { COUNTRY_LIST } from "@/lib/iban";
import { smartSaveOrShare } from "@/lib/download";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { ReceiptPreview } from "@/components/DocumentPreview";
import { NoteModal, TxAlertModal } from "@/components/ReceiptEditModals";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "EUR", label: "Euro (€)" },
  { code: "USD", label: "Dólar estadounidense ($)" },
  { code: "GBP", label: "Libra esterlina (£)" },
  { code: "CHF", label: "Franco suizo (CHF)" },
  { code: "ARS", label: "Peso argentino ($)" },
  { code: "MXN", label: "Peso mexicano ($)" },
  { code: "BRL", label: "Real brasileño (R$)" },
  { code: "JPY", label: "Yen japonés (¥)" },
  { code: "CNY", label: "Yuan chino (¥)" },
  { code: "CAD", label: "Dólar canadiense ($)" },
];
import { Plus, Edit3, Trash2, Search, Users, Wallet, X, Save, RefreshCw, ShieldAlert, Send, KeyRound, Eye, EyeOff, Copy, ArrowRight, Coins, PiggyBank, TrendingUp, Layers, Star, Receipt, Download, Loader2, StickyNote, AlertTriangle } from "lucide-react";

const FX_CURRENCIES = [
  { code: "EUR", label: "Euro (€)" },
  { code: "USD", label: "Dólar estadounidense ($)" },
  { code: "GBP", label: "Libra esterlina (£)" },
];

function KycBadge({ status }) {
  const { t } = useTranslation();
  const labels = {
    verified: t("users.kyc_verified"),
    verification_required: t("users.kyc_required"),
    pending: t("users.kyc_pending"),
    rejected: t("users.kyc_rejected"),
  };
  const map = {
    verified: "bg-emerald-100 text-emerald-700",
    verification_required: "bg-orange-100 text-orange-700",
    pending: "bg-slate-100 text-slate-700",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold ${map[status] || "bg-slate-100 text-slate-700"}`}>{labels[status] || status}</span>;
}

/* ------------ Create User Modal ------------ */
function CreateUserModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", country: "ES",
    role: "user", kyc_status: "verification_required",
    initial_balance: "", currency: "EUR", iban: "", bic: "", bank_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-suggest BIC + bank name from openiban.com when admin types a valid IBAN
  useEffect(() => {
    const iban = (form.iban || "").replace(/\s/g, "").toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return;
    let alive = true;
    const t = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const { data } = await api.post("/iban/validate", { iban });
        if (alive && data?.valid) {
          setForm((f) => ({
            ...f,
            bic: f.bic || data.bic || "",
            bank_name: f.bank_name || data.bank_name || "",
            country: data.country && f.country === "ES" ? data.country : f.country,
          }));
        }
      } catch { /* ignore */ }
      finally { if (alive) setLookupLoading(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [form.iban]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        email: form.email, password: form.password, full_name: form.full_name,
        country: form.country, role: form.role, kyc_status: form.kyc_status,
        currency: form.currency,
        iban: form.iban.replace(/\s/g, "").toUpperCase() || null,
        bic: form.bic.replace(/\s/g, "").toUpperCase() || null,
        bank_name: form.bank_name.trim() || null,
        initial_balance: form.initial_balance ? parseFloat(form.initial_balance) : 0,
      };
      const { data } = await api.post("/admin/users", payload);
      toast.success(t("users.toast_created", { email: data.email }));
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="create-user-modal">
      <form onSubmit={submit} className="bg-white rounded-3xl max-w-xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-slate-900">{t("users.create_title")}</h3>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <label className="block col-span-2">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.full_name")}</span>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="cu-fullname"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.email")}</span>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value.toLowerCase())} data-testid="cu-email"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.password")}</span>
            <input required type="password" minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="cu-password"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.country")}</span>
            <select value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="cu-country"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              {COUNTRY_LIST.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}{c.sepa ? "" : " (SWIFT)"}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.currency")}</span>
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="cu-currency"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.role")}</span>
            <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="cu-role"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              <option value="user">{t("users.role_user")}</option>
              <option value="admin">{t("users.role_admin")}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.kyc_status")}</span>
            <select value={form.kyc_status} onChange={(e) => set("kyc_status", e.target.value)} data-testid="cu-kyc"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              <option value="verification_required">{t("users.kyc_required")}</option>
              <option value="pending">{t("users.kyc_pending")}</option>
              <option value="verified">{t("users.kyc_verified")}</option>
              <option value="rejected">{t("users.kyc_rejected")}</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500 flex items-center gap-2">
              {t("users.col_iban")}
              {lookupLoading && <span className="text-purple-500 text-[10px] normal-case font-sans">{t("users.searching_bank")}</span>}
            </span>
            <input value={form.iban} onChange={(e) => set("iban", e.target.value.toUpperCase())} data-testid="cu-iban"
              placeholder={t("users.iban_placeholder")}
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
            <span className="text-[10px] text-slate-400 mt-1 block">{t("users.iban_hint")}</span>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.bic")}</span>
            <input value={form.bic} onChange={(e) => set("bic", e.target.value.toUpperCase())} data-testid="cu-bic"
              placeholder={t("users.bic_placeholder")}
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
            <span className="text-[10px] text-slate-400 mt-1 block">{t("users.bic_hint")}</span>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.bank")}</span>
            <input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="cu-bankname"
              placeholder={t("users.bank_placeholder")}
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
            <span className="text-[10px] text-slate-400 mt-1 block">{t("users.bank_hint")}</span>
          </label>
          <label className="block col-span-2">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.initial_balance")}</span>
            <input type="number" min="0" step="0.01" value={form.initial_balance} onChange={(e) => set("initial_balance", e.target.value)} data-testid="cu-balance"
              placeholder="0.00"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">{t("users.cancel")}</button>
          <button type="submit" disabled={loading} data-testid="cu-submit"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            {loading ? t("users.creating") : <><Plus size={16} /> {t("users.create_submit")}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------ Edit User Modal ------------ */
function EditUserModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_name: user.full_name, country: user.country || "ES",
    role: user.role, kyc_status: user.kyc_status,
    iban: user.account?.iban || "",
    bic: user.account?.bic || "",
    bank_name: user.account?.bank_name || "",
  });
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const _ra = user.receipt_alert || {};
  const [alertMode, setAlertMode] = useState(_ra.enabled === true ? "on" : _ra.enabled === false ? "off" : "inherit");
  const [alertTitle, setAlertTitle] = useState(_ra.title || "");
  const [alertMsg, setAlertMsg] = useState(_ra.message || "");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-suggest BIC + bank when IBAN changes (skip the initial render value)
  useEffect(() => {
    const iban = (form.iban || "").replace(/\s/g, "").toUpperCase();
    if (iban === (user.account?.iban || "")) return; // unchanged → no lookup
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return;
    let alive = true;
    const t = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const { data } = await api.post("/iban/validate", { iban });
        if (alive && data?.valid) {
          setForm((f) => ({
            ...f,
            bic: data.bic || f.bic,
            bank_name: data.bank_name || f.bank_name,
          }));
        }
      } catch { /* ignore */ }
      finally { if (alive) setLookupLoading(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [form.iban, user.account?.iban]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name, country: form.country,
        role: form.role, kyc_status: form.kyc_status,
        iban: form.iban.replace(/\s/g, "").toUpperCase() || null,
        bic: form.bic.replace(/\s/g, "").toUpperCase() || null,
        bank_name: form.bank_name.trim() || null,
        receipt_alert: alertMode === "inherit"
          ? { enabled: null }
          : { enabled: alertMode === "on", title: alertTitle.trim() || null, message: alertMsg.trim() || null },
      };
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success(t("users.toast_updated"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="edit-user-modal">
      <form onSubmit={submit} className="bg-white rounded-3xl max-w-xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-xl text-slate-900">{t("users.edit_title")}</h3>
            <p className="text-xs font-mono-fin text-slate-500 mt-0.5">{user.email}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <label className="block col-span-2">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.full_name")}</span>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="eu-fullname"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.country")}</span>
            <select value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="eu-country"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              {COUNTRY_LIST.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}{c.sepa ? "" : " (SWIFT)"}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.role")}</span>
            <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="eu-role"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              <option value="user">{t("users.role_user")}</option>
              <option value="admin">{t("users.role_admin")}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.kyc_status")}</span>
            <select value={form.kyc_status} onChange={(e) => set("kyc_status", e.target.value)} data-testid="eu-kyc"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
              <option value="verification_required">{t("users.kyc_required")}</option>
              <option value="pending">{t("users.kyc_pending")}</option>
              <option value="verified">{t("users.kyc_verified")}</option>
              <option value="rejected">{t("users.kyc_rejected")}</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500 flex items-center gap-2">
              IBAN
              {lookupLoading && <span className="text-purple-500 text-[10px] normal-case font-sans">{t("users.searching_bank")}</span>}
            </span>
            <input value={form.iban} onChange={(e) => set("iban", e.target.value.toUpperCase())} data-testid="eu-iban"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
            <span className="text-[10px] text-slate-400 mt-1 block">Modificar el IBAN cambiará el número de cuenta del cliente. Úsalo solo si el cliente lo pide.</span>
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.bic")}</span>
            <input value={form.bic} onChange={(e) => set("bic", e.target.value.toUpperCase())} data-testid="eu-bic"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.bank")}</span>
            <input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="eu-bankname"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </label>

          <div className="col-span-2 mt-1 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-amber-800 font-bold">{t("users.client_alert_title", { defaultValue: "Aviso en comprobantes (este cliente)" })}</span>
              <select value={alertMode} onChange={(e) => setAlertMode(e.target.value)} data-testid="eu-alert-mode"
                className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-xs font-semibold outline-none">
                <option value="inherit">{t("users.client_alert_inherit", { defaultValue: "Heredar (global)" })}</option>
                <option value="on">{t("users.client_alert_on", { defaultValue: "Activar" })}</option>
                <option value="off">{t("users.client_alert_off", { defaultValue: "Desactivar" })}</option>
              </select>
            </div>
            {alertMode === "on" && (
              <div className="mt-3 space-y-2">
                <input value={alertTitle} onChange={(e) => setAlertTitle(e.target.value)} data-testid="eu-alert-title"
                  placeholder={t("users.client_alert_title_ph", { defaultValue: "Título (vacío = usa el global)" })}
                  className="w-full px-3 py-2 rounded-xl border border-amber-300 text-sm bg-white outline-none focus:border-amber-500" />
                <textarea value={alertMsg} onChange={(e) => setAlertMsg(e.target.value)} data-testid="eu-alert-message" rows={2}
                  placeholder={t("users.client_alert_msg_ph", { defaultValue: "Mensaje (vacío = usa el global)" })}
                  className="w-full px-3 py-2 rounded-xl border border-amber-300 text-sm bg-white outline-none focus:border-amber-500" />
              </div>
            )}
            <p className="text-[10px] text-amber-700 mt-2">{t("users.client_alert_hint", { defaultValue: "Aplica a todos los comprobantes de este cliente. Una transferencia individual puede anularlo." })}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">{t("users.cancel")}</button>
          <button type="submit" disabled={loading} data-testid="eu-submit"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            {loading ? t("users.saving") : <><Save size={16} /> {t("users.save_changes")}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------ Balance Modal ------------ */
function BalanceModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("add"); // 'set' | 'add' | 'subtract'
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const current = user.account?.balance ?? 0;
  const cur = user.account?.currency || "EUR";
  const num = parseFloat(amount) || 0;
  const preview = mode === "set" ? num : mode === "add" ? current + num : current - num;

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error(t("users.toast_reason_required")); return; }
    if (num <= 0) { toast.error(t("users.toast_amount_positive")); return; }
    setLoading(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/balance`, { mode, amount: num, reason: reason.trim() });
      toast.success(`${t("users.toast_balance_updated")} · ${fmtMoney(data.old_balance, cur)} → ${fmtMoney(data.new_balance, cur)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="balance-modal">
      <form onSubmit={submit} className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-xl text-slate-900">{t("users.balance_title")}</h3>
            <p className="text-xs font-mono-fin text-slate-500 mt-0.5">{user.full_name} · {user.email}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="mt-5 rounded-2xl bg-slate-50 p-4">
          <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">{t("users.current_balance")}</div>
          <div className="font-display font-bold text-3xl text-slate-900 mt-1 font-mono-fin">{fmtMoney(current, cur)}</div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { k: "set", label: t("users.mode_set"), desc: t("users.mode_set_desc") },
            { k: "add", label: t("users.mode_add"), desc: t("users.mode_add_desc") },
            { k: "subtract", label: t("users.mode_sub"), desc: t("users.mode_sub_desc") },
          ].map((m) => (
            <button key={m.k} type="button" onClick={() => setMode(m.k)} data-testid={`bm-mode-${m.k}`}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                mode === m.k ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
              }`}>
              <div className="font-semibold text-sm text-slate-900">{m.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{m.desc}</div>
            </button>
          ))}
        </div>

        <label className="block mt-4">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.amount_eur")}</span>
          <input required type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bm-amount"
            className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
        </label>

        <label className="block mt-3">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.reason_label")}</span>
          <textarea required value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="bm-reason"
            placeholder={t("users.reason_placeholder")}
            className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
        </label>

        {amount && (
          <div className="mt-4 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/40 p-3 text-xs flex items-center justify-between">
            <span className="text-slate-600">{t("users.resulting")}</span>
            <span className={`font-mono-fin font-bold text-lg ${preview < 0 ? "text-red-600" : "text-emerald-700"}`}>
              {fmtMoney(preview, cur)}
            </span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">{t("users.cancel")}</button>
          <button type="submit" disabled={loading} data-testid="bm-submit"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            <Wallet size={16} /> {loading ? "Aplicando..." : "Confirmar"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------ Page ------------ */
/* ---------- Admin: execute a transfer FROM a user's account (immediate) ---------- */
function AdminTransferModal({ user, onClose, onDone }) {
  const { t } = useTranslation();
  const accCurrency = (user.account?.currency || "EUR").toUpperCase();
  const [form, setForm] = useState({ type: "sepa_standard", beneficiary_name: "", destination: "", amount: "", currency: accCurrency, concept: "" });
  const [fx, setFx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const amt = parseFloat(form.amount) || 0;
    if (!amt || form.currency === accCurrency) { setFx(null); return; }
    let active = true;
    const id = setTimeout(async () => {
      try { const { data } = await api.post("/fx/convert", { amount: amt, from_currency: form.currency, to_currency: accCurrency }); if (active) setFx(data); }
      catch { if (active) setFx(null); }
    }, 350);
    return () => { active = false; clearTimeout(id); };
  }, [form.amount, form.currency, accCurrency]);

  const submit = async () => {
    if (!form.beneficiary_name.trim() || !form.destination.trim() || !(parseFloat(form.amount) > 0)) {
      toast.error(t("users.toast_transfer_fields")); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/transfer`, {
        type: form.type, beneficiary_name: form.beneficiary_name.trim(), destination: form.destination.trim(),
        amount: parseFloat(form.amount), currency: form.currency, concept: form.concept,
      });
      toast.success(`${t("users.toast_transfer_done")} · ${fmtMoney(Math.abs(data.amount), data.currency)}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="admin-transfer-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display font-bold text-xl text-slate-900">{t("users.transfer_title")}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{user.full_name} · saldo {user.account ? fmtMoney(user.account.balance, accCurrency) : "—"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.type")}</span>
              <select value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="atf-type"
                className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 outline-none">
                <option value="sepa_standard">{t("tx.type.sepa_standard")}</option>
                <option value="sepa_instant">{t("tx.type.sepa_instant")}</option>
                <option value="cbu">{t("tx.type.cbu")}</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.currency")}</span>
              <select value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="atf-currency"
                className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 outline-none">
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.beneficiary")}</span>
            <input value={form.beneficiary_name} onChange={(e) => set("beneficiary_name", e.target.value)} data-testid="atf-beneficiary"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.destination")}</span>
            <input value={form.destination} onChange={(e) => set("destination", e.target.value.toUpperCase())} data-testid="atf-destination"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.amount_cur", { cur: form.currency })}</span>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="atf-amount"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 outline-none" />
          </label>
          {fx && (
            <div className="text-sm rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-blue-900 flex items-center gap-2" data-testid="atf-fx">
              <ArrowRight size={14} className="text-blue-600 shrink-0" />
              <span>{t("users.will_debit")} <b>{fmtMoney(fx.converted_amount, accCurrency)}</b> · 1 {form.currency} = {fx.rate} {accCurrency}</span>
            </div>
          )}
          <label className="block">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.concept")}</span>
            <input value={form.concept} onChange={(e) => set("concept", e.target.value)} data-testid="atf-concept"
              className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 outline-none" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">{t("users.cancel")}</button>
          <button onClick={submit} disabled={submitting} data-testid="atf-submit"
            className="inline-flex items-center gap-2 bg-purple-700 hover:bg-purple-800 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            <Send size={15} /> {submitting ? t("users.processing") : t("users.exec_transfer")}
          </button>
        </div>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3">
          <span dangerouslySetInnerHTML={{ __html: t("users.instant_note") }} />
        </p>
      </div>
    </div>
  );
}

/* ---------- Admin: view email + password and reset it ---------- */
function CredentialsModal({ user, onClose, onReset }) {
  const { t } = useTranslation();
  const [newPw, setNewPw] = useState("");
  const [shownPw, setShownPw] = useState(user.plain_password || "");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);

  const genPw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789#$%";
    let p = ""; for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setNewPw(p); setReveal(true);
  };
  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success(t("users.copied")); }
    catch { toast.error(t("users.copy_failed")); }
  };
  const reset = async () => {
    if ((newPw || "").length < 6) { toast.error(t("users.toast_pw_min")); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/password`, { password: newPw });
      setShownPw(data.password); setReveal(true); setNewPw("");
      toast.success(t("users.toast_pw_reset"));
      onReset && onReset();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="credentials-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center"><KeyRound className="text-white" size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{t("users.cred_title")}</h3>
              <p className="text-xs text-slate-500">{user.full_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.email_user")}</span>
            <div className="mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
              <span className="flex-1 font-mono-fin text-sm text-slate-900" data-testid="cred-email">{user.email}</span>
              <button onClick={() => copy(user.email)} className="text-slate-400 hover:text-slate-700"><Copy size={14} /></button>
            </div>
          </div>
          <div>
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.current_password")}</span>
            <div className="mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
              {shownPw ? (
                <span className="flex-1 font-mono-fin text-sm text-slate-900" data-testid="cred-password">{reveal ? shownPw : "•".repeat(shownPw.length)}</span>
              ) : (
                <span className="flex-1 text-xs text-slate-400 italic" data-testid="cred-password-none">{t("users.pw_none")}</span>
              )}
              {shownPw && (
                <>
                  <button onClick={() => setReveal((r) => !r)} className="text-slate-400 hover:text-slate-700">{reveal ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  <button onClick={() => copy(shownPw)} className="text-slate-400 hover:text-slate-700"><Copy size={14} /></button>
                </>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.reset_password")}</span>
            <div className="mt-1.5 flex gap-2">
              <input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t("users.new_pw_placeholder")} data-testid="cred-new-password"
                className="flex-1 px-3 py-2.5 rounded-xl border border-slate-300 text-sm font-mono-fin focus:border-purple-500 outline-none" />
              <button onClick={genPw} className="btn-secondary text-xs whitespace-nowrap" data-testid="cred-generate">{t("users.generate")}</button>
            </div>
            <button onClick={reset} disabled={saving} data-testid="cred-reset-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-purple-700 hover:bg-purple-800 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
              <KeyRound size={15} /> {saving ? t("users.saving") : t("users.reset_password")}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          {t("users.cred_footer")}
        </p>
      </div>
    </div>
  );
}


/* ---------- Admin: change account currency with real-time FX conversion ---------- */
function CurrencyModal({ user, onClose, onDone }) {
  const { t } = useTranslation();
  const current = (user.account?.currency || "EUR").toUpperCase();
  const balance = user.account?.balance ?? 0;
  const [target, setTarget] = useState(current === "EUR" ? "USD" : "EUR");
  const [fx, setFx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target === current) { setFx(null); return; }
    let active = true;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/fx/convert", { amount: balance, from_currency: current, to_currency: target });
        if (active) setFx(data);
      } catch { if (active) setFx(null); }
      finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(id); };
  }, [target, current, balance]);

  const submit = async () => {
    if (target === current) { toast.error(t("users.cur_same")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/currency`, { currency: target });
      toast.success(`${t("users.cur_toast_done")} · ${fmtMoney(data.old_balance, data.old_currency)} → ${fmtMoney(data.new_balance, data.new_currency)}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="currency-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center"><Coins className="text-white" size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{t("users.cur_title")}</h3>
              <p className="text-xs text-slate-500">{user.full_name} · {user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="mt-5 rounded-2xl bg-slate-50 p-4">
          <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">{t("users.cur_current")}</div>
          <div className="font-display font-bold text-3xl text-slate-900 mt-1 font-mono-fin">{fmtMoney(balance, current)}</div>
        </div>

        <label className="block mt-4">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.cur_target")}</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="cur-target"
            className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
            {FX_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>

        {target !== current && (
          <div className="mt-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 p-3 text-sm" data-testid="cur-preview">
            {loading && <span className="text-slate-500 text-xs">{t("users.cur_loading")}</span>}
            {!loading && fx && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-slate-700">
                  <ArrowRight size={14} className="text-amber-600 shrink-0" />
                  <span>{t("users.cur_new_balance")}</span>
                </div>
                <div className="text-right">
                  <div className="font-mono-fin font-bold text-lg text-emerald-700">{fmtMoney(fx.converted_amount, target)}</div>
                  <div className="text-[10px] text-slate-500">1 {current} = {fx.rate} {target}</div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">{t("users.cancel")}</button>
          <button onClick={submit} disabled={submitting || target === current} data-testid="cur-submit"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            <Coins size={15} /> {submitting ? t("users.processing") : t("users.cur_confirm")}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">{t("users.cur_footer")}</p>
      </div>
    </div>
  );
}


/* ---------- Admin: manage a client's multiple accounts ---------- */
const ACCT_TYPE_META = {
  checking: { icon: Wallet, key: "accounts.type_checking" },
  savings: { icon: PiggyBank, key: "accounts.type_savings" },
  yield: { icon: TrendingUp, key: "accounts.type_yield" },
};
const FX3 = ["EUR", "USD", "GBP"];

function AccountRow({ acc, onChanged, t }) {
  const Meta = ACCT_TYPE_META[acc.account_type] || ACCT_TYPE_META.checking;
  const I = Meta.icon;
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'balance' | 'currency'
  const [bal, setBal] = useState({ mode: "set", amount: "", reason: "" });
  const [cur, setCur] = useState(acc.currency);

  const applyBalance = async () => {
    if (!(parseFloat(bal.amount) >= 0) || !bal.reason.trim()) { toast.error(t("users.toast_reason_required")); return; }
    setBusy(true);
    try {
      await api.put(`/admin/accounts/${acc.id}/balance`, { mode: bal.mode, amount: parseFloat(bal.amount), reason: bal.reason.trim() });
      toast.success(t("users.toast_balance_updated")); setMode(null); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const applyCurrency = async () => {
    if (cur === acc.currency) { setMode(null); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/accounts/${acc.id}/currency`, { currency: cur });
      toast.success(`${t("users.cur_toast_done")} · ${fmtMoney(data.old_balance, data.old_currency)} → ${fmtMoney(data.new_balance, data.new_currency)}`);
      setMode(null); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(t("users.acc_delete_confirm"))) return;
    setBusy(true);
    try { await api.delete(`/admin/accounts/${acc.id}`); toast.success(t("users.acc_toast_deleted")); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 p-4" data-testid={`acct-row-${acc.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><I size={18} /></span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
            {acc.label || t(Meta.key)}
            {acc.is_primary && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono-fin font-bold bg-blue-100 text-blue-700 inline-flex items-center gap-1"><Star size={9} /> {t("users.acc_primary")}</span>}
          </div>
          <div className="text-[11px] font-mono-fin text-slate-500">{acc.iban ? groupIban(acc.iban) : t("users.acc_no_iban")}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono-fin font-bold text-slate-900">{fmtMoney(acc.balance, acc.currency)}</div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-3 flex-wrap">
        <button onClick={() => setMode(mode === "balance" ? null : "balance")} data-testid={`acct-balance-${acc.id}`}
          className="text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 rounded-full"><Wallet size={12} /> {t("users.acc_balance_change")}</button>
        <button onClick={() => setMode(mode === "currency" ? null : "currency")} data-testid={`acct-currency-${acc.id}`}
          className="text-xs inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1.5 rounded-full"><Coins size={12} /> {t("users.acc_currency_change")}</button>
        {!acc.is_primary && (
          <button onClick={del} disabled={busy} data-testid={`acct-delete-${acc.id}`}
            className="text-xs inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 rounded-full disabled:opacity-50"><Trash2 size={12} /></button>
        )}
      </div>
      {mode === "balance" && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`acct-balance-form-${acc.id}`}>
          <select value={bal.mode} onChange={(e) => setBal({ ...bal, mode: e.target.value })} className="col-span-1 px-2 py-2 rounded-lg border border-slate-300 text-xs">
            <option value="set">{t("users.mode_set")}</option>
            <option value="add">{t("users.mode_add")}</option>
            <option value="subtract">{t("users.mode_sub")}</option>
          </select>
          <input type="number" min="0" step="0.01" value={bal.amount} onChange={(e) => setBal({ ...bal, amount: e.target.value })} placeholder="0.00" data-testid={`acct-balance-amount-${acc.id}`}
            className="col-span-1 px-2 py-2 rounded-lg border border-slate-300 text-xs font-mono-fin" />
          <input value={bal.reason} onChange={(e) => setBal({ ...bal, reason: e.target.value })} placeholder={t("users.reason_label")} data-testid={`acct-balance-reason-${acc.id}`}
            className="col-span-1 px-2 py-2 rounded-lg border border-slate-300 text-xs" />
          <button onClick={applyBalance} disabled={busy} data-testid={`acct-balance-apply-${acc.id}`} className="col-span-3 bg-emerald-600 text-white rounded-lg py-2 text-xs font-semibold disabled:opacity-50">{t("users.save_changes")}</button>
        </div>
      )}
      {mode === "currency" && (
        <div className="mt-3 flex gap-2" data-testid={`acct-currency-form-${acc.id}`}>
          <select value={cur} onChange={(e) => setCur(e.target.value)} className="flex-1 px-2 py-2 rounded-lg border border-slate-300 text-xs">
            {FX3.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={applyCurrency} disabled={busy} data-testid={`acct-currency-apply-${acc.id}`} className="bg-amber-600 text-white rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50">{t("users.cur_confirm")}</button>
        </div>
      )}
    </div>
  );
}

function AccountsModal({ user, onClose, onChanged }) {
  const { t } = useTranslation();
  const [accs, setAccs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ account_type: "savings", currency: "EUR", initial_balance: "", label: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/users/${user.id}/accounts`); setAccs(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const createAcc = async () => {
    setCreating(true);
    try {
      await api.post(`/admin/users/${user.id}/accounts`, {
        account_type: form.account_type, currency: form.currency,
        initial_balance: form.initial_balance ? parseFloat(form.initial_balance) : 0,
        label: form.label.trim() || null,
      });
      toast.success(t("users.acc_toast_created"));
      setForm({ account_type: "savings", currency: "EUR", initial_balance: "", label: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="accounts-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Layers className="text-white" size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{t("users.accounts_title")}</h3>
              <p className="text-xs text-slate-500">{user.full_name} · {user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="mt-5 space-y-3">
          {loading && <div className="text-center text-slate-500 text-sm py-6">…</div>}
          {!loading && accs.map((a) => <AccountRow key={a.id} acc={a} onChanged={refresh} t={t} />)}
        </div>

        <div className="mt-5 rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/40 p-4">
          <div className="text-xs font-mono-fin uppercase tracking-widest text-blue-700 mb-3 flex items-center gap-2"><Plus size={14} /> {t("users.acc_add")}</div>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} data-testid="acc-new-type"
              className="px-2 py-2 rounded-lg border border-slate-300 text-sm">
              <option value="checking">{t("accounts.type_checking")}</option>
              <option value="savings">{t("accounts.type_savings")}</option>
              <option value="yield">{t("accounts.type_yield")}</option>
            </select>
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="acc-new-currency"
              className="px-2 py-2 rounded-lg border border-slate-300 text-sm">
              {FX3.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min="0" step="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} placeholder={t("users.initial_balance")} data-testid="acc-new-balance"
              className="px-2 py-2 rounded-lg border border-slate-300 text-sm font-mono-fin" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t("users.acc_label_ph")} data-testid="acc-new-label"
              className="px-2 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <button onClick={createAcc} disabled={creating} data-testid="acc-create-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            <Plus size={16} /> {creating ? t("users.acc_creating") : t("users.acc_create")}
          </button>
        </div>
      </div>
    </div>
  );
}

function KycImage({ value, label }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url; let alive = true;
    (async () => {
      if (!value) return;
      if (value.startsWith("data:")) { setSrc(value); return; }
      try {
        const { data } = await api.get(`/uploads/${value}`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        if (alive) setSrc(url);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [value]);
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {!value && <div className="h-40 rounded-xl bg-slate-100 flex items-center justify-center text-xs text-slate-400">—</div>}
      {value && err && <div className="h-40 rounded-xl bg-red-50 flex items-center justify-center text-xs text-red-500">Error</div>}
      {value && !err && (src
        ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={label} className="w-full h-40 object-contain rounded-xl border border-slate-200 bg-slate-50" /></a>
        : <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />)}
    </div>
  );
}

function KycViewModal({ user, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/admin/users/${user.id}/kyc`); setData(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, [user.id]);
  const k = data?.kyc || {};
  const Row = ({ l, v }) => <div className="flex justify-between gap-3 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">{l}</span><span className="font-medium text-slate-900 text-right">{v || "—"}</span></div>;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="kyc-view-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center"><ShieldAlert className="text-white" size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{t("users.kyc_view_title")}</h3>
              <p className="text-xs text-slate-500">{user.full_name} · {user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        {loading && <div className="text-center text-slate-500 text-sm py-8">…</div>}
        {!loading && !k.submitted_at && <div className="text-center text-slate-500 text-sm py-8" data-testid="kyc-empty">{t("users.kyc_none")}</div>}
        {!loading && k.submitted_at && (
          <div className="mt-4 space-y-3">
            <div className="flex gap-3">
              <KycImage value={k.document_front} label={t("kyc.front")} />
              <KycImage value={k.document_back} label={t("kyc.back")} />
            </div>
            <div className="rounded-2xl border border-slate-200 p-3">
              <Row l={t("kyc.doc_type")} v={k.document_type} />
              <Row l={t("kyc.doc_number")} v={k.document_number} />
              <Row l={t("kyc.dob")} v={k.date_of_birth} />
              <Row l={t("kyc.phone")} v={k.phone} />
              <Row l={t("kyc.address")} v={[k.address, k.city, k.postal_code, k.country].filter(Boolean).join(", ")} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function ReceiptsModal({ user, onClose }) {
  const { t, i18n } = useTranslation();
  const { openPdf } = usePdfViewer();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [noteTx, setNoteTx] = useState(null);
  const [alertTx, setAlertTx] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/users/${user.id}/transactions`, { params: { limit: 200 } });
      setTxs(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/transactions`, { params: { limit: 200 } });
        if (alive) setTxs(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user.id]);

  const view = (tx) => openPdf({
    title: `Comprobante ${tx.reference || ""}`.trim(),
    content: <ReceiptPreview tx={tx} />,
    path: `/admin/documents/receipt/${tx.id}?lang=${(i18n.language || "es").split("-")[0]}`,
    filename: `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`,
  });
  const download = async (tx) => {
    if (downloadingId) return;
    setDownloadingId(tx.id);
    const filename = `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
    try { await smartSaveOrShare(`/admin/documents/receipt/${tx.id}?lang=${(i18n.language || "es").split("-")[0]}`, filename); }
    finally { setDownloadingId(null); }
  };

  const saveNote = async (tx, note) => {
    try {
      await api.post(`/admin/transfers/${tx.id}/note`, { note });
      toast.success(note ? t("approvals.toast_note_saved") : t("approvals.toast_note_deleted"));
      setNoteTx(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveAlert = async (tx, { mode, title, message }) => {
    try {
      await api.put(`/admin/transfers/${tx.id}/alert`, { mode, title, message });
      toast.success(t("approvals.toast_alert_saved", { defaultValue: "Aviso actualizado" }));
      setAlertTx(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="receipts-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Receipt className="text-white" size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{t("users.receipts_title", { defaultValue: "Comprobantes" })}</h3>
              <p className="text-xs text-slate-500">{user.full_name} · {user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="mt-5 divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
          {loading && <div className="text-center text-slate-500 text-sm py-8">…</div>}
          {!loading && txs.length === 0 && <div className="text-center text-slate-500 text-sm py-8" data-testid="receipts-empty">{t("users.receipts_empty", { defaultValue: "Sin movimientos" })}</div>}
          {!loading && txs.map((tx) => (
            <div key={tx.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`receipt-row-${tx.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {tx.description}
                  {tx.admin_note && <StickyNote size={13} className="text-blue-500 shrink-0" data-testid={`receipt-has-note-${tx.id}`} />}
                </div>
                <div className="text-[11px] font-mono-fin text-slate-500 truncate">{tx.reference} · {fmtDateTime(tx.executed_at || tx.created_at)}</div>
              </div>
              <div className={`font-mono-fin text-sm font-bold shrink-0 ${tx.amount > 0 ? "text-emerald-600" : "text-slate-900"}`}>
                {tx.amount > 0 ? "+" : ""}{fmtMoney(tx.amount, tx.currency)}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setNoteTx(tx)} data-testid={`receipt-note-${tx.id}`}
                  className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                  <StickyNote size={14} /> <span className="hidden sm:inline">{t("approvals.tx_note_btn", { defaultValue: "Nota" })}</span>
                </button>
                <button onClick={() => setAlertTx(tx)} data-testid={`receipt-alert-${tx.id}`}
                  className="text-slate-500 hover:text-amber-600 hover:bg-amber-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                  <AlertTriangle size={14} /> <span className="hidden sm:inline">{t("approvals.tx_alert_btn", { defaultValue: "Aviso" })}</span>
                </button>
                <button onClick={() => view(tx)} data-testid={`receipt-view-${tx.id}`}
                  className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                  <Eye size={14} /> <span className="hidden sm:inline">{t("users.receipt_view", { defaultValue: "Ver" })}</span>
                </button>
                <button onClick={() => download(tx)} disabled={downloadingId === tx.id} data-testid={`receipt-download-${tx.id}`}
                  className="text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 font-semibold disabled:opacity-50">
                  {downloadingId === tx.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {noteTx && <NoteModal tx={noteTx} onClose={() => setNoteTx(null)} onConfirm={saveNote} />}
      {alertTx && <TxAlertModal tx={alertTx} onClose={() => setAlertTx(null)} onConfirm={saveAlert} />}
    </div>
  );
}

const UserRow = React.memo(function UserRow({ u, isMe, t, onTransfer, onCreds, onBalance, onCurrency, onAccounts, onKyc, onEdit, onDelete, onReceipts }) {
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`row-${u.id}`}>
      <td className="px-5 py-3">
        <div className="font-semibold text-slate-900">{u.full_name}</div>
        <div className="text-[11px] font-mono-fin text-slate-500">{u.email}</div>
      </td>
      <td className="px-5 py-3">
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono-fin font-bold ${
          u.role === "admin" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
        }`}>
          {u.role === "admin" ? "ADMIN" : t("users.role_user")}
        </span>
      </td>
      <td className="px-5 py-3"><KycBadge status={u.kyc_status} /></td>
      <td className="px-5 py-3 font-mono-fin text-[11px] text-slate-600">
        {u.account?.iban ? groupIban(u.account.iban) : "—"}
      </td>
      <td className="px-5 py-3 text-right font-mono-fin font-bold text-slate-900">
        {u.account ? fmtMoney(u.account.balance, u.account.currency) : "—"}
      </td>
      <td className="px-5 py-3 text-slate-500 text-xs">{fmtDateTime(u.created_at)}</td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex gap-1.5">
          <button onClick={() => onTransfer(u)} disabled={!u.account} data-testid={`transfer-${u.id}`}
            className="inline-flex items-center gap-1 bg-purple-700 hover:bg-purple-800 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full disabled:opacity-50" title={t("users.tip_transfer")}>
            <Send size={13} />
          </button>
          <button onClick={() => onCreds(u)} data-testid={`creds-${u.id}`}
            className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full" title={t("users.tip_creds")}>
            <KeyRound size={13} />
          </button>
          <button onClick={() => onBalance(u)} disabled={!u.account} data-testid={`balance-${u.id}`}
            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full disabled:opacity-50" title={t("users.tip_balance")}>
            <Wallet size={13} />
          </button>
          <button onClick={() => onCurrency(u)} disabled={!u.account} data-testid={`currency-${u.id}`}
            className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full disabled:opacity-50" title={t("users.tip_currency")}>
            <Coins size={13} />
          </button>
          <button onClick={() => onAccounts(u)} data-testid={`accounts-${u.id}`}
            className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full" title={t("users.tip_accounts")}>
            <Layers size={13} />
          </button>
          <button onClick={() => onKyc(u)} data-testid={`kyc-${u.id}`}
            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full" title={t("users.kyc_view_title")}>
            <ShieldAlert size={13} />
          </button>
          <button onClick={() => onReceipts(u)} data-testid={`receipts-${u.id}`}
            className="inline-flex items-center gap-1 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full" title={t("users.receipts_title", { defaultValue: "Comprobantes" })}>
            <Receipt size={13} />
          </button>
          <button onClick={() => onEdit(u)} data-testid={`edit-${u.id}`}
            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full" title={t("users.tip_edit")}>
            <Edit3 size={13} />
          </button>
          <button onClick={() => onDelete(u)} disabled={isMe} data-testid={`delete-${u.id}`}
            className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
            title={isMe ? t("users.tip_delete_self") : t("users.tip_delete")}>
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
});

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [credsUser, setCredsUser] = useState(null);
  const [changingCurrency, setChangingCurrency] = useState(null);
  const [managingAccounts, setManagingAccounts] = useState(null);
  const [viewingKyc, setViewingKyc] = useState(null);
  const [viewingReceipts, setViewingReceipts] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [kycFilter, setKycFilter] = useState("all");
  const [withAccounts, setWithAccounts] = useState(false);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (roleFilter !== "all") params.role = roleFilter;
      if (kycFilter !== "all") params.kyc = kycFilter;
      if (withAccounts) params.with_accounts = true;
      const { data } = await api.get("/admin/users", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [query, roleFilter, kycFilter, withAccounts]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!me) return null;
  if (me.role !== "admin") return <Navigate to="/app" replace />;

  const doDelete = async (target) => {
    try {
      await api.delete(`/admin/users/${target.id}`);
      toast.success(t("users.toast_deleted", { email: target.email }));
      setDeleting(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-fin text-xs uppercase tracking-widest text-purple-600">{t("users.eyebrow")}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("users.title")}</h1>
          <p className="text-slate-500 mt-1">{t("users.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("users.search_placeholder")} data-testid="search-input"
              className="pl-9 pr-3 py-2.5 rounded-full border border-slate-300 text-sm w-64 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none" />
          </div>
          <button onClick={fetchAll} disabled={loading} className="btn-secondary text-sm" data-testid="refresh-users-btn">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> {t("users.refresh")}
          </button>
          <button onClick={() => setCreating(true)} data-testid="create-user-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold px-4 py-2.5 rounded-full text-sm">
            <Plus size={16} /> {t("users.create_btn")}
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center" data-testid="users-filters">
        <span className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-400">{t("users.filter_by")}</span>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="filter-role"
          className="px-3 py-2 rounded-full border border-slate-300 text-xs focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
          <option value="all">{t("users.filter_role_all")}</option>
          <option value="user">{t("users.role_user")}</option>
          <option value="admin">{t("users.role_admin")}</option>
        </select>
        <select value={kycFilter} onChange={(e) => setKycFilter(e.target.value)} data-testid="filter-kyc"
          className="px-3 py-2 rounded-full border border-slate-300 text-xs focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none">
          <option value="all">{t("users.filter_kyc_all")}</option>
          <option value="verified">{t("users.kyc_verified")}</option>
          <option value="verification_required">{t("users.kyc_required")}</option>
          <option value="pending">{t("users.kyc_pending")}</option>
          <option value="rejected">{t("users.kyc_rejected")}</option>
        </select>
        <button onClick={() => setWithAccounts((v) => !v)} data-testid="filter-with-accounts"
          className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all ${withAccounts ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"}`}>
          <Layers size={12} className="inline mr-1" /> {t("users.filter_with_accounts")}
        </button>
        {(roleFilter !== "all" || kycFilter !== "all" || withAccounts || search) && (
          <button onClick={() => { setRoleFilter("all"); setKycFilter("all"); setWithAccounts(false); setSearch(""); }} data-testid="filter-clear"
            className="px-3 py-2 rounded-full text-xs text-slate-500 hover:text-slate-800 underline">
            {t("users.filter_clear")}
          </button>
        )}
        <span className="text-[11px] text-slate-400 ml-auto" data-testid="users-count">{t("users.count", { count: rows.length })}</span>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3">{t("users.col_user")}</th>
                <th className="px-5 py-3">{t("users.col_role")}</th>
                <th className="px-5 py-3">KYC</th>
                <th className="px-5 py-3">{t("users.col_iban")}</th>
                <th className="px-5 py-3 text-right">{t("users.col_balance")}</th>
                <th className="px-5 py-3">{t("users.col_created")}</th>
                <th className="px-5 py-3 text-right">{t("users.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">{t("users.loading")}</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center">
                  <Users size={28} className="inline text-slate-300" />
                  <div className="text-slate-500 mt-2 text-xs">{t("users.empty")}</div>
                </td></tr>
              )}
              {!loading && rows.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  isMe={u.id === me.id}
                  t={t}
                  onTransfer={setTransferring}
                  onCreds={setCredsUser}
                  onBalance={setAdjusting}
                  onCurrency={setChangingCurrency}
                  onAccounts={setManagingAccounts}
                  onKyc={setViewingKyc}
                  onEdit={setEditing}
                  onDelete={setDeleting}
                  onReceipts={setViewingReceipts}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-start gap-3 border-l-4 border-purple-600 bg-purple-50 px-4 py-3 text-xs text-slate-700 rounded-r-xl">
        <ShieldAlert size={16} className="text-purple-600 mt-0.5 shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: t("users.audit_note") }} />
      </div>

      {creating && <CreateUserModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); fetchAll(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll(); }} />}
      {adjusting && <BalanceModal user={adjusting} onClose={() => setAdjusting(null)} onSaved={() => { setAdjusting(null); fetchAll(); }} />}
      {transferring && <AdminTransferModal user={transferring} onClose={() => setTransferring(null)} onDone={() => { setTransferring(null); fetchAll(); }} />}
      {credsUser && <CredentialsModal user={credsUser} onClose={() => setCredsUser(null)} onReset={fetchAll} />}
      {changingCurrency && <CurrencyModal user={changingCurrency} onClose={() => setChangingCurrency(null)} onDone={() => { setChangingCurrency(null); fetchAll(); }} />}
      {managingAccounts && <AccountsModal user={managingAccounts} onClose={() => setManagingAccounts(null)} onChanged={fetchAll} />}
      {viewingKyc && <KycViewModal user={viewingKyc} onClose={() => setViewingKyc(null)} />}
      {viewingReceipts && <ReceiptsModal user={viewingReceipts} onClose={() => setViewingReceipts(null)} />}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setDeleting(null)} data-testid="delete-confirm-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><Trash2 className="text-red-600" size={20} /></div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-xl text-slate-900">{t("users.delete_title")}</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <span dangerouslySetInnerHTML={{ __html: t("users.delete_body", { name: deleting.full_name, email: deleting.email }) }} />
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="btn-secondary text-sm">{t("users.cancel")}</button>
              <button onClick={() => doDelete(deleting)} data-testid="delete-confirm-btn"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm">
                <Trash2 size={16} /> {t("users.delete_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
